import clsx from 'clsx';
import { type ForwardedRef, type MutableRefObject, type ReactNode, useMemo } from 'react';
import { useLocalizedMessages } from '../../i18n/index.js';
import { IconChevronLeft, IconChevronRight } from '../../icons/index.js';

import type { ButtonProps } from '../button/button.js';
import type { IconButtonProps } from '../button/icon-button.js';
import type { IconLinkButtonProps } from '../button/icon-link-button.js';
import type { LinkButtonProps } from '../button/link-button.js';
import type { AriaLabelingProps, CommonProps } from '../types.js';

type OmitNever<T extends Record<string, unknown>> = {
  [K in keyof T as T[K] extends never ? never : K]: T[K];
};

type SharedProperties<A, B> = OmitNever<Pick<A & B, keyof A & keyof B>>;

/** @internal */
export type IconButtonProperties = SharedProperties<IconButtonProps, IconLinkButtonProps> & {
  [dataProp: `data-${string}`]: string | number | boolean | undefined;
};

/** @internal */
export type TextButtonProperties = SharedProperties<ButtonProps, LinkButtonProps> & {
  [dataProp: `data-${string}`]: string | number | boolean | undefined;
};

export interface BasePaginationProps extends CommonProps, AriaLabelingProps {
  /** The current page number. */
  currentPage: number;
  /** The total number of pages. */
  pageCount: number;
  /** The `id` of the paginated element. You must provide this prop when pagination controls a subsection of the page (for example, a data table). */
  paginatedElementId?: string;
}

interface PaginationRenderProps {
  navRef?: ForwardedRef<HTMLElement>;
  // `RefObject` doesn't work for this case, it errors in either React 18 or 19 depending on whether `| null` is included in the generic parameter
  // TODO: investigate this more
  listRef?: MutableRefObject<HTMLUListElement | null>;
  renderPreviousButton: (previousButtonProps: IconButtonProperties) => ReactNode;
  renderPageButton: (pageData: PageData) => ReactNode;
  renderNextButton: (nextButtonProps: IconButtonProperties) => ReactNode;
}

interface PageData {
  pageNumber: number;
  isCurrentPage: boolean;
  pageButtonProps: TextButtonProperties;
}

/** @internal */
export function BasePagination({
  currentPage,
  pageCount,
  paginatedElementId,
  UNSAFE_className,
  UNSAFE_style,
  navRef,
  listRef,
  renderPreviousButton,
  renderNextButton,
  renderPageButton,
  ...props
}: BasePaginationProps & PaginationRenderProps) {
  const messages = useLocalizedMessages('pagination');

  const pages: PageData[] = useMemo(() => {
    const visiblePages = getVisiblePages(currentPage, pageCount);
    const firstPageIndex = 0;
    const lastPageIndex = visiblePages.length - 1;

    return visiblePages.map<PageData>((page, index) => {
      const isCurrentPage = typeof page === 'number' && page === currentPage;
      const pageNumber = typeof page === 'number' ? page : page.page;

      const pageButtonProps: TextButtonProperties = {
        'aria-label': messages.format('goToPage', { pageNumber }),
        children: typeof page === 'number' ? page : '…',
        'data-first': index === firstPageIndex || undefined,
        'data-last': index === lastPageIndex || undefined,
        variant: isCurrentPage ? 'primary' : 'secondary',
      };

      return { pageNumber, isCurrentPage, pageButtonProps };
    });
  }, [currentPage, pageCount, messages]);

  const previousButtonProps: IconButtonProperties = {
    icon: <IconChevronLeft />,
    isDisabled: currentPage === 1,
    'aria-label': messages.format('previousPage'),
  };

  const nextButtonProps: IconButtonProperties = {
    icon: <IconChevronRight />,
    isDisabled: currentPage === pageCount,
    'aria-label': messages.format('nextPage'),
  };

  return (
    <nav
      {...props}
      ref={navRef}
      className={clsx('cim-pagination', UNSAFE_className)}
      style={UNSAFE_style}
      aria-controls={paginatedElementId}
    >
      <ul ref={listRef} className="cim-pagination-list">
        <li>{renderPreviousButton(previousButtonProps)}</li>

        {pages.map((pageData) => (
          <li key={pageData.pageNumber}>{renderPageButton(pageData)}</li>
        ))}

        <li>{renderNextButton(nextButtonProps)}</li>
      </ul>
    </nav>
  );
}

function getVisiblePages(currentPage: number, totalPages: number): (number | { type: 'ellipsis'; page: number })[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, { type: 'ellipsis', page: 5 }, totalPages - 1, totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      2,
      { type: 'ellipsis', page: totalPages - 4 },
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    { type: 'ellipsis', page: currentPage - 2 },
    currentPage - 1,
    currentPage,
    currentPage + 1,
    { type: 'ellipsis', page: currentPage + 2 },
    totalPages,
  ];
}
